"use client";
import React from "react";
import { Caveat } from "next/font/google";
import Image from "next/image";
import { motion } from "framer-motion";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export default function FitnessClassesSection({ section }) {
  const title = section?.title || "Raise the bar on classes";
  const items = section?.items || [
    {
      bold: "Flexible sales.",
      text: " Create and sell products as well as services, including membership plans, class passes, personal training, and more! The sky (and your client's endurance) is the limit."
    },
    {
      bold: "Appointment types.",
      text: " Create appointments and let your clients book online, whether they're booking a personal trainer or a gym time slot."
    },
    {
      bold: "Hands-free subscriptions.",
      text: " Set up automated recurring invoicing for your clients, so you always get paid on time."
    }
  ];

  return (
    <section className="py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">

        {/* Section Header with Dumbbell Icon & Bar Circle */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          {/* Dumbbell Icon */}
          <div className="w-12 sm:w-14 h-12 sm:h-14 mx-auto mb-4 text-sky-400">
            <svg viewBox="0 0 64 64" fill="none" className="w-full h-full stroke-current" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
              <rect x="6" y="22" width="8" height="20" rx="3" className="fill-sky-400/20" />
              <rect x="14" y="16" width="6" height="32" rx="2" className="fill-sky-400" />
              <line x1="20" y1="32" x2="44" y2="32" strokeWidth="6" />
              <rect x="44" y="16" width="6" height="32" rx="2" className="fill-sky-400" />
              <rect x="50" y="22" width="8" height="20" rx="3" className="fill-sky-400/20" />
            </svg>
          </div>

          <h2 className={`text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight ${caveat.className}`}>
            <span>Raise the </span>
            <span className="relative inline-block px-3 py-0.5 text-white">
              bar
              <svg 
                className="absolute -top-1 -left-2 w-[125%] h-[135%] text-sky-400 overflow-visible" 
                viewBox="0 0 100 50" 
                fill="none"
              >
                <ellipse cx="50" cy="25" rx="45" ry="20" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
              </svg>
            </span>
            <span> on classes</span>
          </h2>
        </motion.div>

        {/* 3-Column Features Text Grid */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12 mb-16 text-slate-300 text-base sm:text-lg leading-relaxed">
          {items.map((item, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.15 }}
              className="space-y-2"
            >
              <p>
                <strong className="text-white font-bold">{item.bold}</strong>
                {item.text}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Direct Image matching user reference screenshot (NO background container) */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto flex justify-center items-center"
        >
          <Image
            src="/odoo/industries/fitness/classes-appointments.png"
            alt="Raise the bar on classes appointment booking interface"
            width={1024}
            height={458}
            className="w-full h-auto object-contain transition-transform duration-300 hover:scale-[1.01]"
            priority
          />
        </motion.div>

      </div>
    </section>
  );
}
